import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import { resetpassword, sendpasswordotp, verifypasswordotp } from "../services";
import { useNavigate } from "react-router-dom";

export default function ForgetPassword({ onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const navigate = useNavigate();

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleNextEmail = async () => {
    if (!email) {
      return setSnackbar({
        open: true,
        message: "Please enter your email.",
        severity: "error",
      });
    }

    try {
      setLoading(true);
      const response = await sendpasswordotp(email);
      if (response.status === 200) {
        setSnackbar({
          open: true,
          message: "OTP sent successfully!",
          severity: "success",
        });
        setStep(2);
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || "Failed to send OTP.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNextOtp = async () => {
    if (!otp) {
      return setSnackbar({
        open: true,
        message: "Please enter the OTP.",
        severity: "error",
      });
    }

    try {
      setLoading(true);
      const response = await verifypasswordotp(email, otp);
      if (response.status === 200) {
        setSnackbar({
          open: true,
          message: "OTP verified!",
          severity: "success",
        });
        setStep(3);
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || "OTP verification failed.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!password || !confirmPassword) {
      return setSnackbar({
        open: true,
        message: "Please fill all fields.",
        severity: "error",
      });
    }

    if (password !== confirmPassword) {
      return setSnackbar({
        open: true,
        message: "Passwords do not match.",
        severity: "error",
      });
    }

    try {
      setLoading(true);
      const payload = { otp, password };
      const response = await resetpassword(payload);

      if (response.status === 200) {
        setSnackbar({
          open: true,
          message: "Password reset successful!",
          severity: "success",
        });

        // Optional: wait briefly so user can read success message
        
          onClose && onClose();
          onSuccess && onSuccess();
          navigate("/login");
      
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || "Reset failed.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Full-page Loader */}
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(255,255,255,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1300,
          }}
        >
          <CircularProgress size={60} />
        </Box>
      )}

      <Box
        sx={{
          minHeight: "100vh",
          background: "linear-gradient(to right, #0f9ba1, #8ee3a8)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          px: 2,
        }}
      >
        <Paper
          elevation={5}
          sx={{
            p: 4,
            maxWidth: 450,
            width: "100%",
            borderRadius: 2,
            textAlign: "center",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              mb: 3,
              background: "linear-gradient(to right, #0f9ba1, #8ee3a8)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              fontWeight: 600,
            }}
          >
            Forgot Password
          </Typography>

          {/* Step 1: Email */}
          {step === 1 && (
            <>
              <Typography mb={1} align="left">
                Enter your registered email
              </Typography>
              <TextField
                fullWidth
                placeholder="example@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                margin="dense"
              />
              <Button
                fullWidth
                variant="contained"
                sx={{ mt: 2 }}
                onClick={handleNextEmail}
                disabled={loading}
              >
                Send OTP
              </Button>
            </>
          )}

          {/* Step 2: OTP */}
          {step === 2 && (
            <>
              <Typography mb={1} align="left">
                Enter the OTP sent to your email
              </Typography>
              <TextField
                fullWidth
                placeholder="Enter 4-digit OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                margin="dense"
              />
              <Button
                fullWidth
                variant="contained"
                sx={{ mt: 2 }}
                onClick={handleNextOtp}
                disabled={loading}
              >
                Verify OTP
              </Button>
            </>
          )}

          {/* Step 3: Reset Password */}
          {step === 3 && (
            <>
              <Typography mb={1} align="left">
                Enter your new password
              </Typography>
              <TextField
                fullWidth
                type="password"
                placeholder="New password"
                value={password}
                onChange={(e) => setNewPassword(e.target.value)}
                margin="dense"
              />
              <TextField
                fullWidth
                type="password"
                placeholder="Confirm password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                margin="dense"
                sx={{ mt: 2 }}
              />
              <Button
                fullWidth
                variant="contained"
                sx={{ mt: 2 }}
                onClick={handleResetPassword}
                disabled={loading}
              >
                Reset Password
              </Button>
            </>
          )}
        </Paper>
      </Box>

      {/* Snackbar for alerts */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
